import {
  getLocalePath,
  getCountryCode,
  getLanguageCode,
  localizedShopPath,
} from 'utilities/locales';
import { CHECKOUT_ROUTES, SEARCH_PARAM_NAMES } from 'constants/enums';

const LOGIN_APP_PARAM = '&app=hardware-shop';
const SIGNUP_VARIANT_PARAM = '&v=hardware-shop';
const CHECKOUT_LOGIN_REDIRECT_PARAM = 'cart_login_redirect';
const SIGNUP_ONLY_PARAM = 'signup_only';

const IDV_STATUS = Object.freeze({
  SUSPENDED: 'suspended',
  UNVERIFIED: 'unverified',
  VERIFIED: 'verified',
});

const IDV_QUERY_PARAMS = Object.freeze({
  LOGIN: 'idv_login', // Used on cart page when user is prompted to log-in with a cart potentially requiring IDV
  REDIRECT: 'idv_redir', // Used on cart page when logged in user is directed to IDV flow
});

const VERIFY_IDENTITY = 'idvStep';
const CHECKING_VERIFY_IDENTITY = 'checkingIdvStep';

const SIGN_UP_STEPS = {
  [VERIFY_IDENTITY]: 'verify-your-identity',
  [CHECKING_VERIFY_IDENTITY]: 'shop-verify-your-identity',
};

const DASHBOARD_SUBPATHS = {
  DEBIT_CARD_SETUP: '/transfer/square-card',
};

const getSignUpStep = (step) => {
  if (!step) return SIGNUP_VARIANT_PARAM;
  if (!SIGN_UP_STEPS[step]) return '';

  return `&v=${SIGN_UP_STEPS[step]}`;
};

const returnToPage = (returnTo) => {
  if (!returnTo) return '';
  return `&return_to=${encodeURIComponent(window.location.pathname)}`;
};

const buildLegalLink = (predicate) =>
  `${getLocalePath()}/legal/general${predicate}`;

const buildPricingPlanToken = (planToken) =>
  planToken ? `&pricing_plan_token=${planToken}` : '';

const buildGoldenPathParam = (isGoldenPath) => {
  if (!isGoldenPath) return '';
  return '&fromGpHardwareCheckout=true';
};

const buildDashboardLink = (subPath = '') => {
  return `/dashboard${subPath}`;
};

/**
 * Constructs a sign-in link with optional return functionality.
 *
 * @param {Object} options - An object containing the options for the sign-in link.
 * @param {boolean} options.returnTo - Optional. Determines if the page should return to the previous page after sign-in. Default is false.
 * @returns {string} The constructed sign-in link.
 */
const buildSignInLink = ({ returnTo = false, signUpOnly = false }) => {
  const lang = `?lang_code=${getLanguageCode()}`;
  const signUpOnlyParam = signUpOnly ? `&${SIGNUP_ONLY_PARAM}=true` : '';

  return `/login${lang}${LOGIN_APP_PARAM}${signUpOnlyParam}${returnToPage(returnTo)}`;
};

/**
 * Constructs a sign-up link with optional functionality.
 *
 * @param {Object} options - An object containing the options for the sign-up link.
 * @param {boolean} options.returnTo Optional. Determines if page returns to previous page. Default is false.
 * @param {string|null} options.step Optional. Key from SIGN_UP_STEPS mapping. Default is null.
 * @param {string|null} options.pricingPlanToken Optional. pricing_plan_token to set in query params, currently used to link to Bundles w/ payment processing onboarding variant. Default is null.
 * @returns {string} The constructed sign up link.
 */
const buildSignUpLink = ({
  returnTo = false,
  step = null,
  pricingPlanToken = null,
  isGoldenPath = false,
}) => {
  const store = getCountryCode();
  const language = getLanguageCode();
  const localeCode = `${language}-${store}`;
  const stepParam = getSignUpStep(step);
  const returnToParam = returnToPage(returnTo);
  const pricingPlanTokenParam = buildPricingPlanToken(pricingPlanToken);
  const goldenPathParam = buildGoldenPathParam(isGoldenPath);

  return `/signup/${localeCode}?${stepParam}${goldenPathParam}${returnToParam}${pricingPlanTokenParam}`;
};

const buildProductLink = (slug) => `${localizedShopPath()}/products/${slug}`;

const buildDebitCardHelpLink = () =>
  `/help${getLocalePath()}/article/6769-manage-your-square-card}`;

const buildExtBankAcctFaqLink = () =>
  `/help${getLocalePath()}/article/3896-link-and-edit-your-bank-account`;

const buildSignUpTroubleshootingLink = () =>
  `/help${getLocalePath()}/article/5057-signup-troubleshooting`;

const buildSquareHomeLink = (localeOverride = null) =>
  `/${localeOverride || getLocalePath()}`;

// TODO (dadamo): The way we append params to the returnLink returned from `buildSignUpLink` is a bit hacky
// and requires some cleanup. There is no guarantee that `return_to` will be the last query param in the URL.
const buildCheckoutSignInLink = ({ signUpOnly = false }) => {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set(CHECKOUT_LOGIN_REDIRECT_PARAM, 'true');
  return `${buildSignInLink({ returnTo: true, signUpOnly })}?${encodeURIComponent(
    `${searchParams}`,
  )}`;
};

const buildCartLink = () => {
  const urlSearchParams = new URLSearchParams(window.location.search || '');
  // we do not need the "progress" search param when we are on the cart page. The "initial_progress"
  // search param is adequate for the cart page to determine the state of the progress bar in "dashboard mode".
  urlSearchParams.delete(SEARCH_PARAM_NAMES.PROGRESS);
  const finalSearchParams = urlSearchParams.toString()
    ? `?${urlSearchParams.toString()}`
    : '';

  return `${localizedShopPath()}${CHECKOUT_ROUTES.CART}${finalSearchParams}`;
};

const removeCheckoutLoginRedirectParam = (urlSearchParams) => {
  urlSearchParams.delete(CHECKOUT_LOGIN_REDIRECT_PARAM);
  return urlSearchParams;
};

export {
  CHECKING_VERIFY_IDENTITY,
  DASHBOARD_SUBPATHS,
  IDV_QUERY_PARAMS,
  IDV_STATUS,
  VERIFY_IDENTITY,
  CHECKOUT_LOGIN_REDIRECT_PARAM,
  buildDashboardLink,
  buildDebitCardHelpLink,
  buildExtBankAcctFaqLink,
  buildLegalLink,
  buildSignInLink,
  buildSignUpLink,
  buildSignUpTroubleshootingLink,
  buildSquareHomeLink,
  buildProductLink,
  buildCheckoutSignInLink,
  buildCartLink,
  removeCheckoutLoginRedirectParam,
};
